// Chess piece definitions and board setup
export const initializeBoard = () => {
  const board = Array(8).fill(null).map(() => Array(8).fill(null));
  
  // Place pawns
  for (let i = 0; i < 8; i++) {
    board[1][i] = { type: 'pawn', color: 'black' };
    board[6][i] = { type: 'pawn', color: 'white' };
  }
  
  // Place other pieces
  const pieceOrder = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];
  
  for (let i = 0; i < 8; i++) {
    board[0][i] = { type: pieceOrder[i], color: 'black' };
    board[7][i] = { type: pieceOrder[i], color: 'white' };
  }
  
  return board;
};

// Check if a square is within board bounds
const isInBounds = (row, col) => {
  return row >= 0 && row < 8 && col >= 0 && col < 8;
};

// Get all possible moves for a piece
export const isValidMove = (board, row, col, playerColor) => {
  const piece = board[row][col];
  if (!piece || piece.color !== playerColor) return [];
  
  const moves = [];
  
  switch (piece.type) {
    case 'pawn':
      moves.push(...getPawnMoves(board, row, col, piece.color));
      break;
    case 'rook':
      moves.push(...getRookMoves(board, row, col, piece.color));
      break;
    case 'knight':
      moves.push(...getKnightMoves(board, row, col, piece.color));
      break;
    case 'bishop':
      moves.push(...getBishopMoves(board, row, col, piece.color));
      break;
    case 'queen':
      moves.push(...getQueenMoves(board, row, col, piece.color));
      break;
    case 'king':
      moves.push(...getKingMoves(board, row, col, piece.color));
      break;
  }
  
  // Filter out moves that would put own king in check
  return moves.filter(move => {
    const testBoard = makeTestMove(board, row, col, move.row, move.col);
    return !isInCheck(testBoard, piece.color);
  });
};

// Pawn movement logic
const getPawnMoves = (board, row, col, color) => {
  const moves = [];
  const direction = color === 'white' ? -1 : 1;
  const startRow = color === 'white' ? 6 : 1;
  
  // Forward move
  if (isInBounds(row + direction, col) && !board[row + direction][col]) {
    moves.push({ row: row + direction, col });
    
    // Double move from starting position
    if (row === startRow && !board[row + 2 * direction][col]) {
      moves.push({ row: row + 2 * direction, col });
    }
  }
  
  // Diagonal captures
  for (const colOffset of [-1, 1]) {
    const newRow = row + direction;
    const newCol = col + colOffset;
    
    if (isInBounds(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (targetPiece && targetPiece.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  }
  
  return moves;
};

// Rook movement logic
const getRookMoves = (board, row, col, color) => {
  const moves = [];
  const directions = [[0, 1], [0, -1], [1, 0], [-1, 0]];
  
  for (const [dRow, dCol] of directions) {
    for (let i = 1; i < 8; i++) {
      const newRow = row + dRow * i;
      const newCol = col + dCol * i;
      
      if (!isInBounds(newRow, newCol)) break;
      
      const targetPiece = board[newRow][newCol];
      if (!targetPiece) {
        moves.push({ row: newRow, col: newCol });
      } else {
        if (targetPiece.color !== color) {
          moves.push({ row: newRow, col: newCol });
        }
        break;
      }
    }
  }
  
  return moves;
};

// Knight movement logic
const getKnightMoves = (board, row, col, color) => {
  const moves = [];
  const knightMoves = [
    [-2, -1], [-2, 1], [-1, -2], [-1, 2],
    [1, -2], [1, 2], [2, -1], [2, 1]
  ];
  
  for (const [dRow, dCol] of knightMoves) {
    const newRow = row + dRow;
    const newCol = col + dCol;
    
    if (isInBounds(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (!targetPiece || targetPiece.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  }
  
  return moves;
};

// Bishop movement logic
const getBishopMoves = (board, row, col, color) => {
  const moves = [];
  const directions = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
  
  for (const [dRow, dCol] of directions) {
    for (let i = 1; i < 8; i++) {
      const newRow = row + dRow * i;
      const newCol = col + dCol * i;
      
      if (!isInBounds(newRow, newCol)) break;
      
      const targetPiece = board[newRow][newCol];
      if (!targetPiece) {
        moves.push({ row: newRow, col: newCol });
      } else {
        if (targetPiece.color !== color) {
          moves.push({ row: newRow, col: newCol });
        }
        break;
      }
    }
  }
  
  return moves;
};

// Queen movement logic
const getQueenMoves = (board, row, col, color) => {
  return [
    ...getRookMoves(board, row, col, color),
    ...getBishopMoves(board, row, col, color)
  ];
};

// King movement logic
const getKingMoves = (board, row, col, color) => {
  const moves = [];
  const directions = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];
  
  for (const [dRow, dCol] of directions) {
    const newRow = row + dRow;
    const newCol = col + dCol;
    
    if (isInBounds(newRow, newCol)) {
      const targetPiece = board[newRow][newCol];
      if (!targetPiece || targetPiece.color !== color) {
        moves.push({ row: newRow, col: newCol });
      }
    }
  }
  
  return moves;
};

// Make a test move without modifying the original board
const makeTestMove = (board, fromRow, fromCol, toRow, toCol) => {
  const newBoard = board.map(row => [...row]);
  newBoard[toRow][toCol] = newBoard[fromRow][fromCol];
  newBoard[fromRow][fromCol] = null;
  return newBoard;
};

// Make an actual move
export const makeMove = (board, fromRow, fromCol, toRow, toCol, playerColor) => {
  const piece = board[fromRow][fromCol];
  
  if (!piece || piece.color !== playerColor) {
    return { success: false };
  }
  
  const validMoves = isValidMove(board, fromRow, fromCol, playerColor);
  const isValid = validMoves.some(move => move.row === toRow && move.col === toCol);
  
  if (!isValid) {
    return { success: false };
  }
  
  const newBoard = board.map(row => [...row]);
  const capturedPiece = newBoard[toRow][toCol];
  
  newBoard[toRow][toCol] = newBoard[fromRow][fromCol];
  newBoard[fromRow][fromCol] = null;
  
  return {
    success: true,
    newBoard,
    capturedPiece
  };
};

// Find the king of a given color
const findKing = (board, color) => {
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.type === 'king' && piece.color === color) {
        return { row, col };
      }
    }
  }
  return null;
};

// Check if a king is in check
export const isInCheck = (board, color) => {
  const king = findKing(board, color);
  if (!king) return false;
  
  const opponentColor = color === 'white' ? 'black' : 'white';
  
  // Check if any opponent piece can attack the king
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === opponentColor) {
        const moves = getPieceMoves(board, row, col, piece);
        if (moves.some(move => move.row === king.row && move.col === king.col)) {
          return true;
        }
      }
    }
  }
  
  return false;
};

// Get moves for a piece without check validation
const getPieceMoves = (board, row, col, piece) => {
  switch (piece.type) {
    case 'pawn':
      return getPawnMoves(board, row, col, piece.color);
    case 'rook':
      return getRookMoves(board, row, col, piece.color);
    case 'knight':
      return getKnightMoves(board, row, col, piece.color);
    case 'bishop':
      return getBishopMoves(board, row, col, piece.color);
    case 'queen':
      return getQueenMoves(board, row, col, piece.color);
    case 'king':
      return getKingMoves(board, row, col, piece.color);
    default:
      return [];
  }
};

// Check if a player is in checkmate
export const isCheckmate = (board, color) => {
  if (!isInCheck(board, color)) return false;
  
  // Check if any move can get out of check
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === color) {
        const validMoves = isValidMove(board, row, col, color);
        if (validMoves.length > 0) {
          return false;
        }
      }
    }
  }
  
  return true;
};

// Check if the game is in stalemate
export const isStalemate = (board, color) => {
  if (isInCheck(board, color)) return false;
  
  // Check if player has any valid moves
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = board[row][col];
      if (piece && piece.color === color) {
        const validMoves = isValidMove(board, row, col, color);
        if (validMoves.length > 0) {
          return false;
        }
      }
    }
  }
  
  return true;
};